import {PayMethod, PayMethodType} from '@web/core/constants'

/**
 * 네이버 포인트 수단 사용가능 여부
 */
function isAvailableNaverPayMethod(availablePayMethodTypes: PayMethodType[]) {
    return (
        availablePayMethodTypes.includes(PayMethod.NAVERPAY_POINT) ||
        availablePayMethodTypes.includes(PayMethod.REWARD_POINT)
    )
}

/**
 * 네이버 포인트 (결제+입력) 가능 여부
 */
function isAvailableUsingNaverPayPoint({
    remainOrderAmount,
    naverpayPointAmount,
}: {
    // 남은금액
    remainOrderAmount: number
    // 보유 포인트
    naverpayPointAmount: number
}): boolean {
    return remainOrderAmount > 0 && naverpayPointAmount > 0
}

/**
 * 네이버 포인트 사용시 차감 구성
 * - 네이버 포인트는 적립포인트 + 충전포인트로 이루어진다.
 * - 네이버 포인트는 적립 포인트가 우선 차감되고, 충전포인트가 차감된다.
 */
function getComposeUsingNaverPoint({
    usingAmount,
    rewardPointAmount,
    chargePointAmount,
}: {
    // 네이버 사용 포인트
    usingAmount: number
    // 적립 포인트
    rewardPointAmount: number
    // 충전 포인트
    chargePointAmount: number
}) {
    const naverpayPointAmount = rewardPointAmount + chargePointAmount
    // 네이버 사용 포인트는 반드시 적립 + 충전 을 넘을수 없다.
    const usingNaverPoint = Math.min(usingAmount, naverpayPointAmount)
    // 적립 포인트
    const usingRewardPointAmount = Math.min(usingNaverPoint, rewardPointAmount)
    // 충전 포인트
    const usingChargePointAmount = Math.max(usingNaverPoint - usingRewardPointAmount, 0)
    return {
        usingRewardPointAmount,
        usingChargePointAmount,
    }
}

/** 아이템별 사용되는 적립 포인트, 페이머니 계산 */
export const getUsingPointAmountByItem = ({
    items,
    totalPrice,
    usingRewardPointAmount,
    usingTotalChargePointAmount,
}: {
    /** 아이템 목록 */
    items: {
        cpId?: string
        itemId?: string
        price: number
    }[]
    /** 결제 금액 */
    totalPrice: number
    /** 사용되는 적립 포인트 */
    usingRewardPointAmount: number
    /** 사용되는 총 충전 포인트 (chargePoint + rechargePoint) */
    usingTotalChargePointAmount: number
}) => {
    const {usingAmountByItem} = items
        .sort((prev, next) => prev.price - next.price)
        .reduce<{
            remainedRewardUseAmount: number
            remainedPaymoneyUseAmount: number
            usingAmountByItem: {
                cpId: string
                itemId: string
                payAmount: number
                rewardUseAmount: number
                paymoneyUseAmount: number
            }[]
        }>(
            (
                {remainedRewardUseAmount, remainedPaymoneyUseAmount, usingAmountByItem: prevUsingAmountByItem},
                item,
                index,
            ) => {
                const isLastItem = items.length - 1 === index

                const token = item.price / totalPrice
                const rewardUseAmount = Math.floor(usingRewardPointAmount * token)
                const paymoneyUseAmount = Math.floor(usingTotalChargePointAmount * token)

                return {
                    remainedRewardUseAmount: remainedRewardUseAmount - rewardUseAmount,
                    remainedPaymoneyUseAmount: remainedPaymoneyUseAmount - paymoneyUseAmount,
                    usingAmountByItem: [
                        ...prevUsingAmountByItem,
                        {
                            cpId: item.cpId ?? '',
                            itemId: item.itemId ?? '',
                            payAmount: item.price,
                            rewardUseAmount: isLastItem ? remainedRewardUseAmount : rewardUseAmount,
                            paymoneyUseAmount: isLastItem ? remainedPaymoneyUseAmount : paymoneyUseAmount,
                        },
                    ],
                }
            },
            {
                remainedRewardUseAmount: usingRewardPointAmount,
                remainedPaymoneyUseAmount: usingTotalChargePointAmount,
                usingAmountByItem: [],
            },
        )

    return usingAmountByItem
}

export default {
    isAvailableNaverPayMethod,
    isAvailableUsingNaverPayPoint,
    getComposeUsingNaverPoint,
    getUsingPointAmountByItem,
}
